
import React from 'react';
import { ModuleCard } from './ModuleCard';
import { AlertsSection } from './AlertsSection';
import { ModuleStats, Alert, AlertType, AlertSeverity, NavigationPage } from '../types';
import { Language, translations } from '../translations';

interface UserDashboardProps {
  onNavigate: (page: NavigationPage) => void;
  language: Language;
}

export const UserDashboard: React.FC<UserDashboardProps> = ({ onNavigate, language }) => {
  const t = translations[language];

  const USER_MODULES: ModuleStats[] = [
    {
      id: 'market',
      title: t.dashboard.marketMonitor,
      value: '4.2k',
      subValue: t.dashboard.activeIsins,
      status: 'valid',
      trend: 4.8,
      cta: t.dashboard.viewTerminal,
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>'
    },
    {
      id: 'blockchain',
      title: t.dashboard.blockchainIntel,
      value: '2.4k',
      subValue: t.dashboard.addresses,
      status: 'pending',
      trend: 18.2,
      cta: t.dashboard.forensics,
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12V7H5v10h10"/><path d="M16 5V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h2"/><path d="M10 11v.01"/><path d="M17 11h.01"/><path d="M20 11h.01"/><path d="M7 11h.01"/><rect x="15" y="15" width="7" height="5" rx="1"/></svg>'
    },
    {
      id: 'darkweb',
      title: t.dashboard.darkwebLeak,
      value: '3',
      subValue: t.dashboard.leaksFound,
      status: 'error',
      trend: 25.0,
      cta: t.dashboard.remediate,
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 2a8 8 0 0 1 8 8 8 8 0 0 1-8 8 8 8 0 0 1-8-8 8 8 0 0 1 8-8Zm-3 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm6 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm-3 3a3 3 0 0 0-3 3h6a3 3 0 0 0-3-3Z"/></svg>'
    },
    {
      id: 'lei',
      title: t.dashboard.leiCompliance,
      value: '94.2%',
      subValue: t.dashboard.registry,
      status: 'warning',
      trend: -1.2,
      cta: t.dashboard.manageRegistry,
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M3 7v1a3 3 0 0 0 6 0V7"/><path d="M9 7v1a3 3 0 0 0 6 0V7"/><path d="M15 7v1a3 3 0 0 0 6 0V7"/><path d="M19 21v-4a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v4"/><path d="M9 7V5a3 3 0 0 1 6 0v2"/></svg>'
    }
  ];

  const ALERTS_DATA: Alert[] = [
    { id: 'AL-DW-01', type: AlertType.SENTIMENT_SPIKE, severity: AlertSeverity.CRITICAL, entity: 'System Access', description: language === 'fr' ? 'Identifiants administrateur détectés dans un dump public BreachForum.' : 'Admin credentials detected in public BreachForum dump.', date: language === 'fr' ? 'Il y a 2h' : '2h ago' },
    { id: 'AL-ESG-01', type: AlertType.ESG_RISK, severity: AlertSeverity.HIGH, entity: 'Supply Chain', description: language === 'fr' ? 'Pic significatif de rapports médiatiques environnementaux défavorables.' : 'Significant spike in adverse environmental media reports.', date: language === 'fr' ? 'À l\'instant' : 'Just now' },
    { id: 'AL-AML-99', type: AlertType.AML, severity: AlertSeverity.CRITICAL, entity: 'Wallet 0x82...12', description: language === 'fr' ? 'L\'API Crystal a signalé une transaction de rampe d\'accès à haut risque.' : 'Crystal API flagged high-risk on-ramp transaction.', date: language === 'fr' ? 'Il y a 12m' : '12m ago' },
  ];

  return (
    <div className="p-6 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{t.dashboard.title}</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">{t.dashboard.subtitle}</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="hidden lg:flex flex-col text-right mr-4 border-r pr-4 border-slate-200 dark:border-slate-800">
            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t.dashboard.apiUsage}</span>
            <span className="text-lg font-bold text-slate-900 dark:text-white">14,802 / 50k</span>
          </div>
          <button className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-95">
            {t.dashboard.auditExport}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {USER_MODULES.map(module => (
          <div key={module.id} onClick={() => onNavigate(module.id as NavigationPage)} className="cursor-pointer">
            <ModuleCard {...module} />
          </div>
        ))}
      </div>

      <AlertsSection alerts={ALERTS_DATA} />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
           <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest leading-none mb-1">{t.dashboard.exposureTitle}</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500">{t.dashboard.exposureSubtitle}</p>
              </div>
              <div className="flex space-x-2">
                <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[9px] font-black uppercase">{t.dashboard.sentiment}</span>
                <span className="px-3 py-1 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full text-[9px] font-black uppercase">{t.dashboard.amlFlag}</span>
              </div>
           </div>
           <div className="h-64 flex items-end justify-between space-x-2 px-2 relative">
              <div className="absolute inset-0 flex flex-col justify-between opacity-5 dark:opacity-10 py-4">
                <div className="h-px w-full bg-slate-900 dark:bg-white"></div>
                <div className="h-px w-full bg-slate-900 dark:bg-white"></div>
                <div className="h-px w-full bg-slate-900 dark:bg-white"></div>
              </div>
              {[65, 40, 80, 55, 30, 95, 70, 85, 45, 60, 90, 75].map((h, i) => (
                <div key={i} className="flex-1 group relative z-10">
                  <div 
                    style={{ height: `${h}%` }} 
                    className={`rounded-t-lg transition-all duration-500 ease-out ${h > 80 ? 'bg-rose-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                  ></div>
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 dark:bg-slate-700 text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    {h}%
                  </div>
                </div>
              ))}
           </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
           <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-6">{t.dashboard.providerHealth}</h3>
           <div className="space-y-4">
              {[
                { label: 'Crystal API (AML)', uptime: '99.9%', latency: '42ms' },
                { label: 'Opoint (ESG)', uptime: '100%', latency: '120ms' },
                { label: 'ESPY (Sentiment)', uptime: '98.5%', latency: '85ms' },
                { label: 'EmailVerify.io', uptime: '99.9%', latency: '12ms' },
              ].map((api, i) => (
                <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 flex flex-col space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{api.label}</span>
                    <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400">{api.uptime}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{api.latency} {t.dashboard.response}</span>
                    <div className="flex space-x-0.5">
                      {[1,2,3,4,5,6].map(j => <div key={j} className="w-2 h-1 bg-emerald-400 dark:bg-emerald-500 rounded-full"></div>)}
                    </div>
                  </div>
                </div>
              ))}
           </div>
           <button className="w-full mt-6 py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-blue-700 transition-all active:scale-95 shadow-sm">
             {t.dashboard.gatewayManagement}
           </button>
        </div>
      </div>
    </div>
  );
};
